// PURE mode FSM (IMPLEMENTATION §2.7 "Mode FSM", UX §3.2):
//   BOOT → COLD_OPEN (no save) or PLAY (save exists): derived from the sim — the cold open lasts while the
//          Gallery is in practice mode (the tin, then the empty click that ends it and raises the vein);
//   PLAY ⇄ BUILD through the Build tab / Done / Esc / dev key B, once `buildMode` is unlocked (u04); the
//          selection is cleared on every switch (the router does that);
//   Build tools: Move (default) · Chute (from u10, `build.chute`) · Sell. Leaving Build resets the tool.
// Overlay states (+SHEET, +MODAL, +SELECTED) are the UI's and the router's; they do not change the mode.
import type { SimDefs } from '../sim/defs.ts';
import { hasFeature } from '../sim/query.ts';
import type { SimState } from '../sim/state.ts';

export type Mode = 'coldOpen' | 'play' | 'build';
export type Tool = 'move' | 'chute' | 'sell';

export interface ModeCaps {
  coldOpen: boolean;
  canBuild: boolean;
  canChute: boolean;
}

export interface ModeState {
  build: boolean;
  tool: Tool;
}

export const MODE_START: ModeState = { build: false, tool: 'move' };

/** What the sim currently allows (read every frame; cheap). */
export function modeCaps(s: SimState, d: SimDefs): ModeCaps {
  let coldOpen = false;
  for (const b of s.buildings) if (b.type === 'gallery' && b.manual) coldOpen = true;
  const canBuild = hasFeature(s, d, 'buildMode');
  return { coldOpen, canBuild, canChute: canBuild && hasFeature(s, d, 'build.chute') };
}

/** The mode in effect: Build only while it is unlocked and not in the cold open. */
export function effectiveMode(m: ModeState, caps: ModeCaps): Mode {
  if (caps.coldOpen) return 'coldOpen';
  return m.build && caps.canBuild ? 'build' : 'play';
}

/** The tool in effect (a locked Chute tool falls back to Move). */
export function effectiveTool(m: ModeState, caps: ModeCaps): Tool {
  if (effectiveMode(m, caps) !== 'build') return 'move';
  return m.tool === 'chute' && !caps.canChute ? 'move' : m.tool;
}

export function setBuild(m: ModeState, on: boolean, caps: ModeCaps): ModeState {
  const want = on && caps.canBuild && !caps.coldOpen;
  if (want === m.build) return m;
  return { build: want, tool: 'move' };
}

export function setTool(m: ModeState, tool: Tool, caps: ModeCaps): ModeState {
  if (effectiveMode(m, caps) !== 'build') return m;
  if (tool === 'chute' && !caps.canChute) return m;
  return tool === m.tool ? m : { ...m, tool };
}
